import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, onBeforeMount, ref } from "vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import AffiliateMainInfoItem from "./AffiliateMainInfoItem.vue";
import SvgTrue from "@/assets/icons/check.svg";
import SvgFalse from "@/assets/icons/close.svg";
import { getEmployeesOfferVerticals } from "@/api/offers";
import { IContractType, ITrafficSource } from "@/api/types/user";
import {
    createAffiliateTag,
    getAffiliateTags,
    getContractTypes,
    getTrafficSource,
    patchEmployeesOfferVerticals,
    patchEmployeesTrafficSource,
    saveAffiliateTags,
} from "@/api/user";
import { Affiliate } from "@/services/affiliates/Affiliate";
import { showServerError } from "@/utils";
import {
    AFFILIATE_EDIT_KEY,
    AFFILIATE_INFO_EDIT_TYPE,
    AFFILIATE_INFO_TYPE,
    EDITABLE_FIELD,
} from "./types";
import { useCardEdit } from "./useCardEdit";

interface Props {
    affiliate: Affiliate;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AccountSettingsCard',
  props: {
    affiliate: null
  },
  emits: ["saved"],
  setup(__props: any, { emit }: { emit: ({ (e: 'saved'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
    affiliate: Affiliate;
}



const ctx = inject(AFFILIATE_EDIT_KEY)!;

const OWNED_KEYS: EDITABLE_FIELD[] = [
    EDITABLE_FIELD.VERTICALS,
    EDITABLE_FIELD.TAGS,
    EDITABLE_FIELD.TRAFFIC_SOURCES,
    EDITABLE_FIELD.CONTRACT_TYPE,
];

// === Reference data ===

const verticalsItems = ref<{ id: number; name: string }[]>([]);
const trafficSourceItems = ref<ITrafficSource[]>([]);
const contractTypeItems = ref<IContractType[]>([]);

onBeforeMount(async () => {
    try {
        await Promise.all([
            getEmployeesOfferVerticals().then(r => verticalsItems.value = r),
            getTrafficSource().then(r => trafficSourceItems.value = r),
            getContractTypes().then(r => contractTypeItems.value = r),
        ]);
    } catch (err) {
        showServerError(err, 'Ошибка загрузки данных');
    }
});

// === Update methods ===

const updateVerticals = async () => {
    await patchEmployeesOfferVerticals(props.affiliate.id!, {
        verticals: ctx.editableFields.value[EDITABLE_FIELD.VERTICALS],
    });
};

const updateTrafficSources = async () => {
    await patchEmployeesTrafficSource(props.affiliate.id!, {
        ids: ctx.editableFields.value[EDITABLE_FIELD.TRAFFIC_SOURCES],
    });
};

const updateTags = async () => {
    await saveAffiliateTags(
        { tags: ctx.editableFields.value[EDITABLE_FIELD.TAGS] },
        props.affiliate.id!,
    );
};

// === Computed helpers ===

const getTranslatedVerticalsValue = computed(() => {
    if (verticalsItems.value.length === 0) return '';
    return props.affiliate.verticals.map(i => verticalsItems.value.find(v => v.id === i.id)!.name);
});

const getTranslatedTrafficValue = computed(() => {
    return props.affiliate.traffic_sources.map(i => i.name);
});

const getTranslatedTagsValue = computed(() => {
    return props.affiliate.tags?.map(i => i.name);
});

const getTranslatedContractTypeValue = computed(() => {
    return props.affiliate.contract_type?.name! || '';
});

const getMarkingData = computed(() => {
    const trueData = { icon: SvgTrue, text: 'Маркируется', class: '--true' };
    const falseData = { icon: SvgFalse, text: 'Не маркируется', class: '--false' };
    return props.affiliate.contract_type?.marking ? trueData : falseData;
});

// === Card edit ===

const { isEdit, isLoading, isDisableEdit, editInfo, resetInfo } = useCardEdit({
    affiliateId: () => props.affiliate.id!,
    getItems: () => items.value,
    editableFields: () => ctx.editableFields.value,
    getChangedValues: () => ctx.getChangedValues.value,
    resetFields: ctx.resetFields,
    ownedKeys: OWNED_KEYS,
    onAfterSave: () => emit('saved'),
});

// === Items ===

const items = computed(() => {
    const baseItems = {
        verticals: {
            type: AFFILIATE_INFO_TYPE.CHIP_ARRAY,
            name: 'Вертикаль',
            value: ctx.editableFields.value[EDITABLE_FIELD.VERTICALS],
            isEditable: true,
            editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
            selectItems: verticalsItems.value,
            selectData: { itemText: 'name', isMultiple: true, isClearable: true },
            textValue: getTranslatedVerticalsValue.value || [],
            editMethod: ctx.editItem,
            updateMethod: updateVerticals,
        },
        tags: {
            type: AFFILIATE_INFO_TYPE.CHIP_ARRAY,
            name: 'Теги',
            value: ctx.editableFields.value[EDITABLE_FIELD.TAGS],
            selectItems: props.affiliate.tags,
            isEditable: true,
            editType: AFFILIATE_INFO_EDIT_TYPE.TAG_SELECT,
            tagSelectData: {
                getTagsMethod: getAffiliateTags,
                createTagMethod: createAffiliateTag,
            },
            textValue: getTranslatedTagsValue.value || [],
            editMethod: ctx.editItem,
            updateMethod: updateTags,
        },
        traffic_sources: {
            type: AFFILIATE_INFO_TYPE.CHIP_ARRAY,
            name: 'Источники трафика',
            value: ctx.editableFields.value[EDITABLE_FIELD.TRAFFIC_SOURCES],
            isEditable: true,
            editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
            selectItems: trafficSourceItems.value,
            selectData: { itemText: 'name', isMultiple: true, isClearable: true },
            textValue: getTranslatedTrafficValue.value || [],
            editMethod: ctx.editItem,
            updateMethod: updateTrafficSources,
        },
    };

    const contractItem = {
        type: AFFILIATE_INFO_TYPE.CONTRACT_TYPE,
        name: 'Тип договора',
        value: ctx.editableFields.value[EDITABLE_FIELD.CONTRACT_TYPE],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: contractTypeItems.value,
        icon: getMarkingData.value.icon,
        tooltipText: getMarkingData.value.text,
        class: getMarkingData.value.class,
        selectData: {
            itemText: 'name',
            itemValue: 'id',
            isClearable: true,
            clearValue: -1,
        },
        textValue: getTranslatedContractTypeValue.value || '',
        editMethod: ctx.editItem,
    };

    return props.affiliate.is_master
        ? { contract_type: contractItem, ...baseItems }
        : baseItems;
});

return { __sfc: true,props, emit, ctx, OWNED_KEYS, verticalsItems, trafficSourceItems, contractTypeItems, updateVerticals, updateTrafficSources, updateTags, getTranslatedVerticalsValue, getTranslatedTrafficValue, getTranslatedTagsValue, getTranslatedContractTypeValue, getMarkingData, isEdit, isLoading, isDisableEdit, editInfo, resetInfo, items, BaseEditableCard, BaseList, BaseListItem, AffiliateMainInfoItem }
}

})